/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/shared/types';

export const nbNO: LocalizationResource = {
  locale: 'nb-NO',
  apiKeys: {
    action__add: 'Legg til ny nøkkel',
    action__search: 'Søk i nøkler',
    copySecret: {
      formButtonPrimary__copyAndClose: 'Kopier og lukk',
      formHint: 'Av sikkerhetshensyn kan vi ikke tillate deg å se det igjen senere.',
      formTitle: 'Kopier API-nøkkelen "{{name}}" din nå',
    },
    createdAndExpirationStatus__expiresOn:
      "Opprettet {{ createdDate | shortDate('nb-NO') }} • Utløper {{ expiresDate | longDate('nb-NO') }}",
    createdAndExpirationStatus__never: "Opprettet {{ createdDate | shortDate('nb-NO') }} • Utløper aldri",
    detailsTitle__emptyRow: 'Ingen API-nøkler funnet',
    formButtonPrimary__add: 'Opprett nøkkel',
    formFieldCaption__expiration__expiresOn: 'Utløper {{ date }}',
    formFieldCaption__expiration__never: 'Denne nøkkelen utløper aldri',
    formFieldOption__expiration__180d: '180 dager',
    formFieldOption__expiration__1d: '1 dag',
    formFieldOption__expiration__1y: '1 år',
    formFieldOption__expiration__30d: '30 dager',
    formFieldOption__expiration__60d: '60 dager',
    formFieldOption__expiration__7d: '7 dager',
    formFieldOption__expiration__90d: '90 dager',
    formFieldOption__expiration__never: 'Aldri',
    formHint: 'Oppgi et navn for å generere en ny nøkkel. Du kan tilbakekalle den når som helst.',
    formTitle: 'Legg til ny API-nøkkel',
    lastUsed__days: '{{days}}d siden',
    lastUsed__hours: '{{hours}}t siden',
    lastUsed__minutes: '{{minutes}}m siden',
    lastUsed__months: '{{months}}mnd siden',
    lastUsed__seconds: '{{seconds}}s siden',
    lastUsed__years: '{{years}}å siden',
    menuAction__revoke: 'Tilbakekall nøkkel',
    revokeConfirmation: {
      confirmationText: 'Tilbakekall',
      formButtonPrimary__revoke: 'Tilbakekall nøkkel',
      formHint: 'Er du sikker på at du vil slette denne hemmelige nøkkelen?',
      formTitle: 'Tilbakekall den hemmelige nøkkelen "{{apiKeyName}}"?',
      inputLabel: undefined,
    },
    tableHeader__actions: undefined,
    tableHeader__lastUsed: undefined,
    tableHeader__name: undefined,
  },
  backButton: 'Tilbake',
  badge__activePlan: 'Aktiv',
  badge__banned: undefined,
  badge__canceledEndsAt: "Kansellert • Slutter {{ date | shortDate('nb-NO') }}",
  badge__currentPlan: 'Nåværende plan',
  badge__default: 'Standard',
  badge__endsAt: "Slutter {{ date | shortDate('nb-NO') }}",
  badge__expired: 'Utløpt',
  badge__freeTrial: 'Gratis prøveperiode',
  badge__otherImpersonatorDevice: 'Annen imitators enhet',
  badge__pastDueAt: "Forfalt {{ date | shortDate('nb-NO') }}",
  badge__pastDuePlan: 'Forfalt',
  badge__primary: 'Primær',
  badge__renewsAt: "Fornyes {{ date | shortDate('nb-NO') }}",
  badge__requiresAction: 'Krever handling',
  badge__startsAt: "Starter {{ date | shortDate('nb-NO') }}",
  badge__thisDevice: 'Denne enheten',
  badge__trialEndsAt: "Prøveperioden slutter {{ date | shortDate('nb-NO') }}",
  badge__unverified: 'Ikke verifisert',
  badge__upcomingPlan: 'Kommende',
  badge__userDevice: 'Brukerens enhet',
  badge__you: 'Du',
  billing: {
    accountCredit: 'Kontokreditt',
    addPaymentMethod__label: 'Legg til betalingsmetode',
    alwaysFree: 'Alltid gratis',
    annually: 'Årlig',
    availableFeatures: 'Tilgjengelige funksjoner',
    billedAnnually: 'Faktureres årlig',
    billedAnnuallyOnly: 'Kun årlig fakturering',
    billedMonthly: undefined,
    billedMonthlyOnly: 'Kun månedlig fakturering',
    cancelFreeTrial: 'Avbryt gratis prøveperiode',
    cancelFreeTrialAccessUntil:
      "Prøveperioden din vil være aktiv til {{ date | longDate('nb-NO') }}. Etter det mister du tilgang til prøvefunksjonene. Du vil ikke bli belastet.",
    cancelFreeTrialTitle: 'Avbryt gratis prøveperiode for {{plan}}-planen?',
    cancelSubscription: 'Avbryt abonnement',
    cancelSubscriptionAccessUntil:
      "Du kan fortsette å bruke '{{plan}}'-funksjonene til {{ date | longDate('nb-NO') }}, etter det vil du ikke lenger ha tilgang.",
    cancelSubscriptionNoCharge: 'Du vil ikke bli belastet for dette abonnementet.',
    cancelSubscriptionPastDue:
      'Abonnementet ditt avsluttes umiddelbart og du vil miste tilgang til alle planfunksjoner. Du vil bli bedt om å betale det forfalte beløpet på ditt neste abonnement.',
    cancelSubscriptionTitle: 'Avbryt {{plan}}-abonnementet?',
    cannotSubscribeMonthly:
      'Du kan ikke abonnere på denne planen med månedlig betaling. For å abonnere på denne planen må du velge årlig betaling.',
    cannotSubscribeUnrecoverable:
      'Du kan ikke abonnere på denne planen. Ditt eksisterende abonnement er dyrere enn denne planen.',
    checkout: {
      description__paymentSuccessful: 'Betalingen din var vellykket.',
      description__subscriptionSuccessful: 'Ditt nye abonnement er klart.',
      downgradeNotice:
        'Du beholder ditt nåværende abonnement og dets funksjoner til slutten av faktureringsperioden, deretter byttes du til dette abonnementet.',
      emailForm: {
        subtitle: 'Før du kan fullføre kjøpet må du legge til en e-postadresse som kvitteringer sendes til.',
        title: 'Legg til en e-postadresse',
      },
      lineItems: {
        title__freeTrialEndsAt: 'Prøveperioden slutter',
        title__paymentMethod: 'Betalingsmetode',
        title__statementId: 'Faktura-ID',
        title__subscriptionBegins: 'Abonnementet starter',
        title__totalPaid: 'Totalt betalt',
      },
      pastDueNotice: 'Ditt forrige abonnement var forfalt, uten betaling.',
      perMonth: 'per måned',
      title: 'Kasse',
      title__paymentSuccessful: 'Betalingen var vellykket!',
      title__subscriptionSuccessful: 'Fullført!',
      title__trialSuccess: 'Prøveperioden er startet!',
      totalDueAfterTrial: 'Totalt forfaller etter at prøveperioden slutter om {{days}} dager',
      totalDuePerPeriod: undefined,
    },
    credit: 'Kreditt',
    creditRemainder: 'Kreditt for gjenstående del av ditt nåværende abonnement.',
    defaultFreePlanActive: 'Du er på gratis-planen',
    free: 'Gratis',
    getStarted: 'Kom i gang',
    highlightedPlanBadge: 'Populær',
    keepFreeTrial: 'Behold gratis prøveperiode',
    keepSubscription: 'Behold abonnement',
    manage: 'Administrer',
    manageSubscription: 'Administrer abonnement',
    month: 'Måned',
    monthAbbreviation: 'mnd',
    monthPerUnit: 'Måned per {{unitName}}',
    monthly: 'Månedlig',
    pastDue: 'Forfalt',
    pay: 'Betal {{amount}}',
    payerCreditRemainder: 'Kreditt fra kontosaldo.',
    paymentMethod: {
      applePayDescription: {
        annual: 'Årlig betaling',
        monthly: 'Månedlig betaling',
      },
      dev: {
        anyNumbers: 'Vilkårlige tall',
        cardNumber: 'Kortnummer',
        cvcZip: 'CVC, postnummer',
        developmentMode: 'Utviklingsmodus',
        expirationDate: 'Utløpsdato',
        testCardInfo: 'Testkortinformasjon',
      },
    },
    paymentMethods__label: 'Betalingsmetoder',
    pricingTable: {
      billingCycle: 'Faktureringsperiode',
      included: 'Inkludert',
      seatCost: {
        additionalSeats: '({{additionalTierFeePerBlockAmount}}/{{periodAbbreviation}} for ekstra)',
        freeUpToSeats: 'Gratis for opptil {{endsAfterBlock}} seter',
        includedSeats: '{{includedSeats}} seter inkludert',
        perSeat: '{{feePerBlockAmount}}/{{periodAbbreviation}} per sete',
        tooltip: {
          additionalSeatsEach: 'Ekstra seter koster {{feePerBlockAmount}}/{{period}} per stykk.',
          firstSeatsIncludedInPlan: 'De første {{endsAfterBlock}} setene er inkludert i planen.',
          freeForUpToSeats: 'Gratis for opptil {{endsAfterBlock}} seter.',
        },
        unlimitedSeats: 'Ubegrenset antall seter',
        upToSeats: 'Opptil {{endsAfterBlock}} seter',
      },
    },
    proratedDiscount: undefined,
    prorationCredit: 'Forholdsmessig kreditt',
    reSubscribe: 'Abonner på nytt',
    seatBreakdownIncludedPlural: undefined,
    seatBreakdownIncludedSingular: undefined,
    seatBreakdownPlural: undefined,
    seatBreakdownSingular: undefined,
    seats: 'Seter',
    seatsWithLimit: undefined,
    seeAllFeatures: 'Se alle funksjoner',
    startFreeTrial: 'Start gratis prøveperiode',
    startFreeTrial__days: 'Start {{days}}-dagers gratis prøveperiode',
    subscribe: 'Abonner',
    subscriptionDetails: {
      beginsOn: 'Starter den',
      currentBillingCycle: 'Nåværende faktureringsperiode',
      endsOn: 'Slutter den',
      firstPaymentAmount: 'Første betalingsbeløp',
      firstPaymentOn: 'Første betaling den',
      nextPaymentAmount: 'Neste betalingsbeløp',
      nextPaymentOn: 'Neste betaling den',
      pastDueAt: 'Forfalt den',
      renewsAt: 'Fornyes den',
      subscribedOn: 'Abonnert den',
      title: 'Abonnement',
      trialEndsOn: 'Prøveperioden slutter den',
      trialStartedOn: 'Prøveperioden startet den',
    },
    subtotal: 'Delsum',
    subtotalRenewal: undefined,
    switchPlan: 'Bytt til denne planen',
    switchToAnnual: 'Bytt til årlig',
    switchToAnnualWithAnnualPrice: 'Bytt til årlig {{price}} / år',
    switchToMonthly: 'Bytt til månedlig',
    switchToMonthlyWithPrice: 'Bytt til månedlig {{price}} / måned',
    totalDue: 'Totalt å betale',
    totalDuePerPeriod: undefined,
    totalDueToday: 'Totalt å betale i dag',
    viewFeatures: 'Se funksjoner',
    viewPayment: 'Se betaling',
    year: 'År',
    yearAbbreviation: 'år',
    yearPerUnit: 'År per {{unitName}}',
  },
  configureSSO: {
    activate: {
      activateButton: undefined,
      activeSubtitle: undefined,
      activeTitle: undefined,
      doneButton: undefined,
      skipButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    changeProviderDialog: {
      cancelButton: undefined,
      confirmButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    configureStep: {
      activeConnectionWarning: {
        dismiss: undefined,
        title: undefined,
      },
      attributeMappingTable: {
        badges: {
          optional: undefined,
          required: undefined,
        },
      },
      samlCustom: {
        assignUsersStep: {
          headerSubtitle: undefined,
          paragraph: undefined,
        },
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              attributeName: undefined,
              userAttribute: undefined,
            },
            rows: {
              email: {
                attributeName: undefined,
                userAttribute: undefined,
              },
              firstName: {
                attributeName: undefined,
                userAttribute: undefined,
              },
              lastName: {
                attributeName: undefined,
                userAttribute: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
        },
        createAppStep: {
          createAppInstructions: {
            paragraph: undefined,
          },
          headerSubtitle: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
          },
        },
        mainHeaderTitle: undefined,
      },
      samlGoogle: {
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              appAttribute: undefined,
              googleAttribute: undefined,
            },
            rows: {
              email: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
              firstName: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
              lastName: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
          step1: undefined,
          step2: undefined,
        },
        configureUserAccess: {
          assignUsersInstructions: {
            paragraph1: undefined,
            paragraph2: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
          },
          headerSubtitle: undefined,
        },
        createAppStep: {
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataFile: {
            description: undefined,
            fileUploaded: undefined,
            label: undefined,
            removeFile: undefined,
            replaceFile: undefined,
            uploadFile: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataFile: undefined,
          },
        },
        mainHeaderTitle: undefined,
        serviceProviderStep: {
          headerSubtitle: undefined,
          nameIdInstructions: {
            step1: undefined,
            step2: undefined,
          },
          paragraph: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
          title: undefined,
        },
      },
      samlMicrosoft: {
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              attribute: undefined,
              claimName: undefined,
              value: undefined,
            },
            copyClaimName: undefined,
            copyClaimNameCopied: undefined,
            rows: {
              email: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
              firstName: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
              lastName: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          step1: undefined,
          step2: undefined,
          title: undefined,
        },
        createAppStep: {
          assignUsersInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
            title: undefined,
          },
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: {
              label: undefined,
              subSteps: {
                appName: undefined,
                create: undefined,
                nonGallery: undefined,
              },
            },
            title: undefined,
          },
          headerSubtitle: undefined,
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
          },
        },
        mainHeaderTitle: undefined,
        serviceProviderStep: {
          headerSubtitle: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
          step1: undefined,
          step2: undefined,
          step3: undefined,
          step4: undefined,
          step5: undefined,
          step6: undefined,
          title: undefined,
        },
      },
      samlOkta: {
        assignUsersStep: {
          assignUsersInstructions: {
            paragraph: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
          },
          headerSubtitle: undefined,
        },
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              expression: undefined,
              name: undefined,
            },
            rows: {
              email: {
                expression: undefined,
                name: undefined,
              },
              firstName: {
                expression: undefined,
                name: undefined,
              },
              lastName: {
                expression: undefined,
                name: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
          step1: undefined,
          step2: undefined,
        },
        createAppStep: {
          completeSamlIntegrationInstructions: {
            step1: undefined,
            step2: undefined,
            title: undefined,
          },
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
          serviceProviderInstructions: {
            paragraph1: undefined,
            paragraph2: undefined,
            serviceProviderFields: {
              acsUrl: {
                label: undefined,
              },
              spEntityId: {
                label: undefined,
              },
            },
            title: undefined,
          },
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
          },
        },
        mainHeaderTitle: undefined,
      },
    },
    missingManageEnterpriseConnectionsPermission: {
      subtitle: 'Kontakt organisasjonens administrator for å oppgradere tillatelsene dine.',
      title: 'Du har ikke tillatelse til å administrere enkeltpålogging (SSO)',
    },
    navbar: {
      title: 'Konfigurer enkeltpålogging (SSO)',
    },
    organizationDomainsStep: {
      domainCard: {
        badge__expired: undefined,
        badge__unverified: 'Ikke verifisert',
        badge__verified: 'Verifisert',
        expiredAtLabel: undefined,
        expiredLabel: undefined,
        removeButtonTooltip__lastVerifiedDomain: undefined,
        removeButtonTooltip__lastVerifiedDomainActive: undefined,
        txtRecord: {
          hostLabel: 'Vert / Navn',
          instructions:
            'Legg til denne TXT-posten hos DNS-leverandøren din. Vi verifiserer automatisk når posten er aktiv.',
          typeLabel: 'Type',
          valueLabel: 'Verdi',
        },
        verifiedAtLabel: "Verifisert den {{ date | shortDate('nb-NO') }}",
        verifyAgainButton: undefined,
      },
      domainSuggestion: {
        formButtonPrimary__add: 'Legg til {{domain}}',
        messageLabel: 'E-posten din bruker {{domain}}. Vil du legge den til?',
      },
      formButtonPrimary__add: 'Legg til',
      formFieldInputPlaceholder__domain: 'Legg til domene',
      formFieldLabel__domain: 'Domene',
      removeDomainDialog: {
        cancelButton: undefined,
        removeButton: undefined,
        subtitle__active: undefined,
        subtitle__inactive: undefined,
        title: undefined,
      },
      subtitle: 'Legg til og bekreft eierskapet til domenene organisasjonen din bruker for å logge på.',
      title: 'Legg til SSO-domener',
    },
    resetConnectionDialog: {
      cancelButton: undefined,
      confirmationFieldLabel: undefined,
      confirmationFieldPlaceholder: undefined,
      resetButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    selectProviderStep: {
      saml: {
        customSaml: 'Egendefinert SAML-leverandør',
        google: undefined,
        groupLabel: 'SAML',
        microsoft: undefined,
        okta: 'Okta Workforce',
      },
      subtitle: 'Velg leverandøren du skal konfigurere SSO for.',
      title: 'Velg leverandør',
      warning: 'Når en leverandør er valgt, kan du ikke endre igjen før konfigurasjonen er ferdig',
    },
    testConfigurationStep: {
      error__noSuccessfulTestRun: undefined,
      subtitle: undefined,
      testResults: {
        actionLabel__refresh: undefined,
        empty: {
          subtitle: undefined,
          title: undefined,
        },
        polling: undefined,
        status__failed: undefined,
        status__pending: undefined,
        status__success: undefined,
        title: undefined,
      },
      testRunDetails: {
        howToFix: {
          actionLabel__viewDocumentation: undefined,
          oauth_access_denied: {
            description: undefined,
          },
          oauth_fetch_user_error: {
            intro: undefined,
            step1: undefined,
            step2: undefined,
          },
          oauth_token_exchange_error: {
            description: undefined,
          },
          saml_email_address_domain_mismatch: {
            description: undefined,
          },
          saml_response_relaystate_missing: {
            description: undefined,
          },
          saml_user_attribute_missing: {
            intro: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
          },
          sectionTitle: undefined,
        },
        parsedUserInfo: {
          email: undefined,
          firstName: undefined,
          sectionTitle: undefined,
        },
        runDetails: {
          actionLabel__copied: undefined,
          actionLabel__copy: undefined,
          errorCode: undefined,
          fullMessage: undefined,
          sectionTitle: undefined,
          status: undefined,
          timestamp: undefined,
        },
        title: undefined,
      },
      testUrl: {
        actionLabel__open: undefined,
      },
      title: undefined,
    },
  },
  createOrganization: {
    formButtonSubmit: 'Opprett organisasjon',
    invitePage: {
      formButtonReset: 'Hopp over',
    },
    title: 'Opprett organisasjon',
  },
  dates: {
    lastDay: "I går kl. {{ date | timeString('nb-NO') }}",
    next6Days: "{{ date | weekday('nb-NO','long') }} kl. {{ date | timeString('nb-NO') }}",
    nextDay: "I morgen kl. {{ date | timeString('nb-NO') }}",
    numeric: "{{ date | numeric('nb-NO') }}",
    previous6Days: "Sist {{ date | weekday('nb-NO','long') }} kl. {{ date | timeString('nb-NO') }}",
    sameDay: "I dag kl. {{ date | timeString('nb-NO') }}",
  },
  dividerText: 'eller',
  footerActionLink__alternativePhoneCodeProvider: 'Send kode via SMS i stedet',
  footerActionLink__useAnotherMethod: 'Bruk en annen metode',
  footerPageLink__help: 'Hjelp',
  footerPageLink__privacy: 'Personvern',
  footerPageLink__terms: 'Vilkår',
  formButtonPrimary: 'Fortsett',
  formButtonPrimary__verify: 'Verifiser',
  formFieldAction__forgotPassword: 'Glemt passord?',
  formFieldError__matchingPasswords: 'Passordene stemmer overens.',
  formFieldError__notMatchingPasswords: 'Passordene stemmer ikke overens.',
  formFieldError__verificationLinkExpired: 'Verifikasjonslenken har utløpt. Vennligst be om en ny lenke.',
  formFieldHintText__optional: 'Valgfritt',
  formFieldHintText__slug: 'En slug er en menneskelesbar ID som må være unik. Den brukes ofte i URL-er.',
  formFieldInputPlaceholder__apiKeyDescription: 'Forklar hvorfor du genererer denne nøkkelen',
  formFieldInputPlaceholder__apiKeyExpirationDate: 'Velg dato',
  formFieldInputPlaceholder__apiKeyName: 'Skriv inn navnet på den hemmelige nøkkelen',
  formFieldInputPlaceholder__backupCode: 'Skriv inn sikkerhetskode',
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'Slett konto',
  formFieldInputPlaceholder__emailAddress: 'Skriv inn e-postadressen din',
  formFieldInputPlaceholder__emailAddress_username: 'Skriv inn e-post eller brukernavn',
  formFieldInputPlaceholder__emailAddresses:
    'Skriv inn eller lim inn én eller flere e-postadresser, separert med mellomrom eller komma',
  formFieldInputPlaceholder__firstName: 'Fornavn',
  formFieldInputPlaceholder__lastName: 'Etternavn',
  formFieldInputPlaceholder__organizationDomain: 'eksempel.com',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'deg@eksempel.com',
  formFieldInputPlaceholder__organizationName: 'Organisasjonsnavn',
  formFieldInputPlaceholder__organizationSlug: 'min-org',
  formFieldInputPlaceholder__password: 'Skriv inn passordet ditt',
  formFieldInputPlaceholder__phoneNumber: 'Skriv inn telefonnummeret ditt',
  formFieldInputPlaceholder__signUpPassword: 'Opprett et passord',
  formFieldInputPlaceholder__username: 'Skriv inn brukernavnet ditt',
  formFieldInput__emailAddress_format: 'Eksempelformat: navn@eksempel.com',
  formFieldLabel__apiKey: 'API-nøkkel',
  formFieldLabel__apiKeyDescription: 'Beskrivelse',
  formFieldLabel__apiKeyExpiration: 'Utløper',
  formFieldLabel__apiKeyName: 'Navn på hemmelig nøkkel',
  formFieldLabel__automaticInvitations: 'Skru på automatiske invitasjoner for dette domenet',
  formFieldLabel__backupCode: 'Sikkerhetskode',
  formFieldLabel__confirmDeletion: 'Bekreftelse',
  formFieldLabel__confirmPassword: 'Bekreft passord',
  formFieldLabel__currentPassword: 'Nåværende passord',
  formFieldLabel__emailAddress: 'E-postadresse',
  formFieldLabel__emailAddress_username: 'E-postadresse eller brukernavn',
  formFieldLabel__emailAddresses: 'E-postadresser',
  formFieldLabel__firstName: 'Fornavn',
  formFieldLabel__lastName: 'Etternavn',
  formFieldLabel__newPassword: 'Nytt passord',
  formFieldLabel__organizationDomain: 'Domene',
  formFieldLabel__organizationDomainDeletePending: 'Slett ventende invitasjoner og forslag',
  formFieldLabel__organizationDomainEmailAddress: 'Verifikasjon e-postadresse',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'Oppgi en e-postadresse under dette domenet for å motta en kode og verifisere domenet.',
  formFieldLabel__organizationName: 'Organisasjonsnavn',
  formFieldLabel__organizationSlug: 'Slug URL',
  formFieldLabel__passkeyName: 'Navn på passnøkkel',
  formFieldLabel__password: 'Passord',
  formFieldLabel__phoneNumber: 'Telefonnummer',
  formFieldLabel__role: 'Rolle',
  formFieldLabel__signOutOfOtherSessions: 'Logg ut fra alle andre enheter',
  formFieldLabel__username: 'Brukernavn',
  identityPreviewEditButton__emailAddress: undefined,
  identityPreviewEditButton__identifier: undefined,
  identityPreviewEditButton__phoneNumber: undefined,
  impersonationFab: {
    action__signOut: 'Logg ut',
    title: 'Logget inn som {{identifier}}',
  },
  lastAuthenticationStrategy: 'Sist brukt',
  maintenanceMode:
    'Vi utfører vedlikehold for øyeblikket, men ikke bekymre deg, det bør ikke ta mer enn noen minutter.',
  membershipRole__admin: 'Administrator',
  membershipRole__basicMember: 'Medlem',
  membershipRole__guestMember: 'Gjest',
  oauthConsent: {
    action__allow: undefined,
    action__deny: undefined,
    offlineAccessNotice: undefined,
    redirectNotice: undefined,
    redirectUriModal: {
      subtitle: undefined,
      title: undefined,
    },
    scopeList: {
      title: undefined,
    },
    subtitle: undefined,
    viewFullUrl: undefined,
    warning: undefined,
  },
  organizationList: {
    action__createOrganization: 'Lag organisasjon',
    action__invitationAccept: 'Bli med',
    action__suggestionsAccept: 'Spør om å bli med',
    createOrganization: 'Lag Organisasjon',
    invitationAcceptedLabel: 'Blitt med',
    subtitle: 'for å fortsette til {{applicationName}}',
    suggestionsAcceptedLabel: 'Venter på godkjenning',
    title: 'Velg en bruker',
    titleWithoutPersonal: 'Velg en organisasjon',
  },
  organizationProfile: {
    apiKeysPage: {
      title: 'API-nøkler',
    },
    badge__automaticInvitation: 'Automatisk invitasjon',
    badge__automaticSuggestion: 'Automatisk forslag',
    badge__enterpriseSso: undefined,
    badge__manualInvitation: 'Ingen automatisk registrering',
    badge__unverified: 'Uverifisert',
    billingPage: {
      accountCreditsSection: {
        title: undefined,
        viewHistory: undefined,
      },
      creditHistoryPage: {
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
      },
      paymentHistorySection: {
        empty: 'Ingen betalingshistorikk',
        notFound: 'Betalingsforsøk ikke funnet',
        tableHeader__amount: 'Beløp',
        tableHeader__date: 'Dato',
        tableHeader__status: 'Status',
      },
      paymentMethodsSection: {
        actionLabel__default: 'Sett som standard',
        actionLabel__remove: 'Fjern',
        add: 'Legg til ny betalingsmetode',
        addSubtitle: 'Legg til en ny betalingsmetode for kontoen din.',
        cancelButton: 'Avbryt',
        formButtonPrimary__add: 'Legg til betalingsmetode',
        formButtonPrimary__pay: 'Betal {{amount}}',
        payWithTestCardButton: 'Betal med testkort',
        removeMethod: {
          messageLine1: '{{identifier}} vil bli fjernet fra denne kontoen.',
          messageLine2:
            'Du vil ikke lenger kunne bruke denne betalingsmetoden, og eventuelle gjentakende abonnementer avhengig av den vil slutte å fungere.',
          successMessage: '{{paymentMethod}} har blitt fjernet fra kontoen din.',
          title: 'Fjern betalingsmetode',
        },
        title: 'Betalingsmetoder',
      },
      start: {
        headerTitle__payments: 'Betalinger',
        headerTitle__plans: 'Planer',
        headerTitle__statements: 'Kontoutskrifter',
        headerTitle__subscriptions: 'Abonnement',
      },
      statementsSection: {
        empty: 'Ingen kontoutskrifter å vise',
        itemCaption__paidForPlan: 'Betalt for {{plan}} {{period}}-plan',
        itemCaption__payerCredit: 'Kreditt fra kontosaldo',
        itemCaption__proratedCredit: 'Forholdsmessig kreditt for delvis bruk av forrige abonnement',
        itemCaption__subscribedAndPaidForPlan: 'Abonnert og betalt for {{plan}} {{period}}-plan',
        notFound: 'Kontoutskrift ikke funnet',
        tableHeader__amount: 'Beløp',
        tableHeader__date: 'Dato',
        title: 'Kontoutskrifter',
        totalPaid: 'Totalt betalt',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: 'Administrer',
        actionLabel__newSubscription: 'Abonner på en plan',
        actionLabel__switchPlan: 'Bytt plan',
        includedSeatsUsage: undefined,
        overview: undefined,
        paidSeatsUsage: undefined,
        seatLimit: undefined,
        seatLimitAndIncludedSeats: undefined,
        tableHeader__edit: 'Rediger',
        tableHeader__plan: 'Plan',
        tableHeader__startDate: 'Startdato',
        title: 'Abonnement',
      },
      subscriptionsSection: {
        actionLabel__default: 'Administrer',
      },
      switchPlansSection: {
        title: 'Bytt plan',
      },
      title: 'Fakturering',
    },
    createDomainPage: {
      subtitle:
        'Legg til domenet som skal verifiseres. Brukere med e-postadresser på dette domenet kan automatisk bli med i organisasjonen eller be om å få bli med.',
      title: 'Legg til domene',
    },
    invitePage: {
      detailsTitle__inviteFailed: 'Invitasjonene kunne ikke sendes. Fiks følgende og prøv igjen:',
      formButtonPrimary__continue: 'Send invitasjoner',
      formButtonPrimary__purchaseSeats: undefined,
      selectDropdown__role: 'Velg rolle',
      subtitle: 'Inviter nye medlemmer til denne organisasjonen',
      successMessage: 'Invitasjoner er sendt',
      title: 'Inviter medlemmer',
    },
    membersPage: {
      action__invite: 'Inviter',
      action__search: 'Søk',
      activeMembersTab: {
        menuAction__remove: 'Fjern medlem',
        tableHeader__actions: 'Handlinger',
        tableHeader__joined: 'Ble med',
        tableHeader__role: 'Rolle',
        tableHeader__user: 'Bruker',
      },
      alerts: {
        roleSetMigrationInProgress: {
          subtitle: 'Vi oppdaterer tilgjengelige roller. Når det er gjort, vil du kunne oppdatere roller igjen.',
          title: 'Roller er midlertidig låst',
        },
      },
      detailsTitle__emptyRow: 'Ingen medlemmer å vise',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'Inviter brukere ved å koble et e-postdomene til organisasjonen din. Alle som registrerer seg med et matchende e-postdomene vil kunne bli med i organisasjonen når som helst.',
          headerTitle: 'Automatiske invitasjoner',
          primaryButton: 'Administrer verifiserte domener',
        },
        table__emptyRow: 'Ingen invitasjoner å vise',
      },
      invitedMembersTab: {
        menuAction__revoke: 'Tilbakekall invitasjon',
        tableHeader__invited: 'Invitert',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle:
            'Brukere som registrerer seg med et matchende e-postdomene, vil kunne se et forslag om å be om å bli med i organisasjonen din.',
          headerTitle: 'Automatiske forslag',
          primaryButton: 'Administrer verifiserte domener',
        },
        menuAction__approve: 'Godta',
        menuAction__reject: 'Avslå',
        tableHeader__requested: 'Tilgangsforespøsler',
        table__emptyRow: 'Ingen forsespørsler å vise',
      },
      start: {
        headerTitle__invitations: 'Invitasjoner',
        headerTitle__members: 'Medlemmer',
        headerTitle__requests: 'Forespørsler',
      },
    },
    navbar: {
      apiKeys: 'API-nøkler',
      billing: 'Fakturering',
      description: 'Administrer organisasjonen din.',
      general: 'Generelt',
      members: 'Medlemmer',
      security: undefined,
      title: 'Organisasjon',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: 'Du har ikke tillatelse til å administrere fakturering for denne organisasjonen.',
        planMembershipLimitExceeded:
          'Organisasjonen din har {{count}} medlemmer (inkludert ventende invitasjoner). Denne planen tillater kun {{limit}} medlemmer.',
      },
      title: 'Planer',
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'Skriv "{{organizationName}}" under for å bekrefte.',
          messageLine1: 'Er du sikker på at du vil slette denne organisasjonen?',
          messageLine2: 'Denne handlingen er permanent og kan ikke reverseres.',
          successMessage: 'Du har slettet organisasjonen.',
          title: 'Slett organisasjonen',
        },
        leaveOrganization: {
          actionDescription: 'Skriv "{{organizationName}}" under for å fortsette.',
          messageLine1:
            'Er du sikker på at du vil forlate denne organisasjonen? Du vil miste tilgangen til denne organisasjonen og dens applikasjoner.',
          messageLine2: 'Denne handlingen er permanent og kan ikke reverseres.',
          successMessage: 'Du har forlatt organisasjonen.',
          title: 'Forlat organisasjonen',
        },
        title: 'Fare',
      },
      domainSection: {
        menuAction__manage: 'Administrer',
        menuAction__remove: 'Slett',
        menuAction__verify: 'Verifiser',
        primaryButton: 'Legg til domene',
        subtitle:
          'Tillat brukere å bli med i organisasjonen automatisk eller be om å bli med basert på et verifisert e-postdomene.',
        title: 'Verifiserte domener',
      },
      successMessage: 'Organisasjonen er oppdatert.',
      title: 'Organisasjonsprofil',
    },
    removeDomainPage: {
      messageLine1: 'E-postdomenet {{domain}} vil bli fjernet.',
      messageLine2: 'Brukere vil ikke kunne bli med i organisasjonen automatisk etter dette.',
      successMessage: '{{domain}} har blitt fjernet.',
      title: 'Fjern domene',
    },
    securityPage: {
      removeDialog: {
        confirmButton: undefined,
        subtitle: undefined,
        title: undefined,
      },
      ssoSection: {
        badge__active: undefined,
        badge__inProgress: undefined,
        badge__inactive: undefined,
        badge__unconfigured: undefined,
        descriptionLine1: undefined,
        domainLabel: undefined,
        menuAction__activate: undefined,
        menuAction__deactivate: undefined,
        menuAction__edit: undefined,
        menuAction__remove: undefined,
        primaryButton__continueConfiguration: undefined,
        primaryButton__startConfiguration: undefined,
        title: undefined,
        tooltip: undefined,
        tooltipLabel: undefined,
        tooltip__noRole: undefined,
      },
      title: undefined,
    },
    start: {
      headerTitle__general: 'Generelt',
      headerTitle__members: 'Medlemmer',
      membershipSeatUsageLabel: '{{count}} av {{limit}} seter brukt',
      profileSection: {
        primaryButton: 'Oppdater profil',
        title: 'Organisasjonsprofil',
        uploadAction__title: 'Logo',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'Å fjerne dette domenet vil påvirke inviterte brukere.',
        removeDomainActionLabel__remove: 'Fjern domene',
        removeDomainSubtitle: 'Fjern domenet fra dine verifiserte domener',
        removeDomainTitle: 'Fjern domene',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'Brukere blir automatisk invitert til å bli med i organisasjonen når de registrerer seg og kan bli med når som helst.',
        automaticInvitationOption__label: 'Automatiske invitasjoner',
        automaticSuggestionOption__description:
          'Brukere mottar et forslag om å be om å bli med, men må godkjennes av en administrator før de kan bli med i organisasjonen.',
        automaticSuggestionOption__label: 'Automatiske forslag',
        calloutInfoLabel: 'Å endre påmeldingsmodus vil kun påvirke nye brukere.',
        calloutInvitationCountLabel: 'Ventende invitasjoner sendt til brukere: {{count}}',
        calloutSuggestionCountLabel: 'Ventende forslag sendt til brukere: {{count}}',
        manualInvitationOption__description: 'Brukere kan kun bli invitert manuelt til organisasjonen.',
        manualInvitationOption__label: 'Ingen automatisk registrering',
        subtitle: 'Velg hvordan brukere fra dette domenet kan bli med i organisasjonen.',
      },
      start: {
        headerTitle__danger: 'Fare',
        headerTitle__enrollment: 'Registreringsalternativer',
      },
      subtitle: 'Domenet {{domain}} har blitt verifisert. Fortsett ved å velge registreringsmodus.',
      title: 'Oppdater {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'Skriv inn verifiseringskoden som ble sendt til e-postadressen din',
      formTitle: 'Verifiseringskode',
      resendButton: 'Ikke mottatt kode? Send på nytt',
      subtitle: 'Domenet {{domainName}} må verifiseres gjennom e-post.',
      subtitleVerificationCodeScreen:
        'En verifiseringskode har blitt sendt til {{emailAddress}}. Skriv inn koden for å fortsette.',
      title: 'Verifiser domene',
    },
  },
  organizationSwitcher: {
    action__closeOrganizationSwitcher: 'Lukk organisasjonsvelger',
    action__createOrganization: 'Opprett organisasjon',
    action__invitationAccept: 'Bli med',
    action__manageOrganization: 'Administrer organisasjon',
    action__openOrganizationSwitcher: 'Åpne organisasjonsvelger',
    action__suggestionsAccept: 'Spør om å bli med',
    notSelected: 'Ingen organisasjon valgt',
    personalWorkspace: 'Personlig arbeidsområde',
    suggestionsAcceptedLabel: 'Venter på godkjenning',
  },
  paginationButton__next: 'Neste',
  paginationButton__previous: 'Forrige',
  paginationRowText__displaying: 'Viser',
  paginationRowText__of: 'av',
  reverification: {
    alternativeMethods: {
      actionLink: 'Få hjelp',
      actionText: 'Har du ingen av disse?',
      blockButton__backupCode: 'Bruk en sikkerhetskode',
      blockButton__emailCode: 'Send e-postkode til {{identifier}}',
      blockButton__passkey: 'Bruk passnøkkelen din',
      blockButton__password: 'Fortsett med passordet ditt',
      blockButton__phoneCode: 'Send SMS-kode til {{identifier}}',
      blockButton__totp: 'Bruk autentiseringsappen din',
      getHelp: {
        blockButton__emailSupport: 'Send e-post til brukerstøtte',
        content:
          'Hvis du har problemer med å verifisere kontoen din, send oss en e-post, og vi vil jobbe med deg for å gjenopprette tilgangen så snart som mulig.',
        title: 'Få hjelp',
      },
      subtitle: 'Opplever du problemer? Du kan bruke hvilken som helst av disse metodene for verifisering.',
      title: 'Bruk en annen metode',
    },
    backupCodeMfa: {
      subtitle: 'Skriv inn sikkerhetskoden du fikk da du satte opp to-trinns verifisering',
      title: 'Skriv inn en sikkerhetskode',
    },
    emailCode: {
      formTitle: 'Verifiseringskode',
      resendButton: 'Ikke mottatt kode? Send på nytt',
      subtitle: 'Skriv inn koden sendt til e-posten din for å fortsette',
      title: 'Verifisering påkrevd',
    },
    noAvailableMethods: {
      message: 'Kan ikke fortsette med verifiseringen. Ingen passende autentiseringsfaktor er konfigurert',
      subtitle: 'En feil oppstod',
      title: 'Kan ikke verifisere kontoen din',
    },
    passkey: {
      blockButton__passkey: 'Bruk passnøkkelen din',
      subtitle:
        'Bruk av passnøkkelen bekrefter identiteten din. Enheten din kan be om fingeravtrykk, ansiktsgjenkjenning eller skjermlås.',
      title: 'Bruk passnøkkelen din',
    },
    password: {
      actionLink: 'Bruk en annen metode',
      subtitle: 'Skriv inn ditt nåværende passord for å fortsette',
      title: 'Verifisering påkrevd',
    },
    phoneCode: {
      formTitle: 'Verifiseringskode',
      resendButton: 'Ikke mottatt kode? Send på nytt',
      subtitle: 'Skriv inn koden sendt til telefonen din for å fortsette',
      title: 'Verifisering påkrevd',
    },
    phoneCodeMfa: {
      formTitle: 'Verifiseringskode',
      resendButton: 'Ikke mottatt kode? Send på nytt',
      subtitle: 'Skriv inn koden sendt til telefonen din for å fortsette',
      title: 'Verifisering påkrevd',
    },
    totpMfa: {
      formTitle: 'Verifiseringskode',
      subtitle: 'Skriv inn koden generert av autentiseringsappen din for å fortsette',
      title: 'Verifisering påkrevd',
    },
  },
  searchInput: {
    action__clear: undefined,
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'Legg til konto',
      action__signOutAll: 'Logg ut av alle kontoer',
      subtitle: 'Velg kontoen du ønsker å fortsette med.',
      title: 'Velg konto',
    },
    alternativeMethods: {
      actionLink: 'Få hjelp',
      actionText: 'Har du ingen av disse?',
      blockButton__backupCode: 'Bruk en sikkerhetskopi-kode',
      blockButton__emailCode: 'Send e-postkode til {{identifier}}',
      blockButton__emailLink: 'Send lenke til {{identifier}}',
      blockButton__passkey: 'Logg inn med passnøkkelen din',
      blockButton__password: 'Logg inn med passordet ditt',
      blockButton__phoneCode: 'Send SMS-kode til {{identifier}}',
      blockButton__totp: 'Bruk autentiseringsappen din',
      getHelp: {
        blockButton__emailSupport: 'Kontakt kundestøtte via e-post',
        content:
          'Hvis du har problemer med å logge inn på kontoen din, kan du sende oss en e-post, og vi vil jobbe med deg for å gjenopprette tilgangen så snart som mulig.',
        title: 'Få hjelp',
      },
      subtitle: 'Opplever du problemer? Du kan bruke hvilken som helst av disse metodene for å logge inn.',
      title: 'Bruk en annen metode',
    },
    alternativePhoneCodeProvider: {
      formTitle: 'Verifiseringskode',
      resendButton: 'Ikke mottatt kode? Send på nytt',
      subtitle: 'for å fortsette til {{applicationName}}',
      title: 'Sjekk {{provider}}',
    },
    backupCodeMfa: {
      subtitle: 'for å fortsette til {{applicationName}}',
      title: 'Skriv inn en sikkerhetskopi-kode',
    },
    emailCode: {
      formTitle: 'Verifiseringskode',
      resendButton: 'Send kode på nytt',
      subtitle: 'for å fortsette til {{applicationName}}',
      title: 'Sjekk e-posten din',
    },
    emailCodeMfa: {
      formTitle: 'Sjekk e-posten din',
      resendButton: 'Ikke mottatt kode? Send på nytt',
      subtitle: 'for å fortsette til {{applicationName}}',
      title: 'Sjekk e-posten din',
    },
    emailLink: {
      clientMismatch: {
        subtitle: 'For å fortsette, åpne verifiseringslenken på enheten og nettleseren du startet innloggingen fra',
        title: 'Verifiseringslenken er ugyldig for denne enheten',
      },
      expired: {
        subtitle: 'Gå tilbake til den opprinnelige fanen for å fortsette.',
        title: 'Denne verifiseringslenken er utløpt',
      },
      failed: {
        subtitle: 'Gå tilbake til den opprinnelige fanen for å fortsette.',
        title: 'Denne verifiseringslenken er ugyldig',
      },
      formSubtitle: 'Bruk verifiseringslenken som er sendt til e-postadressen din',
      formTitle: 'Verifiseringslenke',
      loading: {
        subtitle: 'Du blir omdirigert snart',
        title: 'Logger inn...',
      },
      resendButton: 'Send lenke på nytt',
      subtitle: 'for å fortsette til {{applicationName}}',
      title: 'Sjekk e-posten din',
      unusedTab: {
        title: 'Du kan lukke denne fanen',
      },
      verified: {
        subtitle: 'Du blir omdirigert snart',
        title: 'Innloggingen var vellykket',
      },
      verifiedSwitchTab: {
        subtitle: 'Gå tilbake til den opprinnelige fanen for å fortsette',
        subtitleNewTab: 'Gå tilbake til den nyåpnede fanen for å fortsette',
        titleNewTab: 'Logget inn på en annen fane',
      },
    },
    emailLinkMfa: {
      formSubtitle: 'Bruk bekreftelseslenken som ble sendt til din e-post',
      resendButton: 'Har du ikke mottatt lenken? Send på nytt',
      subtitle: 'for å fortsette til {{applicationName}}',
      title: 'Sjekk din e-post',
    },
    enterpriseConnections: {
      subtitle: 'Velg bedriftskontoen du ønsker å fortsette med.',
      title: 'Velg bedriftskonto',
    },
    forgotPassword: {
      formTitle: 'Tilbakestill passord-kode',
      resendButton: 'Send kode på nytt',
      subtitle: 'for å tilbakestille passordet ditt',
      subtitle_email: 'Først, skriv inn koden som ble sendt til e-posten din',
      subtitle_phone: 'Først, skriv inn koden som ble sendt til telefonen din',
      title: 'Tilbakestill passord',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'Tilbakestill passordet ditt',
      label__alternativeMethods: 'Eller logg inn med en annen metode.',
      title: 'Glemt passord?',
    },
    newDeviceVerificationNotice:
      'Du logger inn fra en ny enhet. Vi ber om verifisering for å holde kontoen din sikker.',
    noAvailableMethods: {
      message: 'Kan ikke fortsette med innloggingen. Det er ingen tilgjengelige autentiseringsfaktorer.',
      subtitle: 'En feil oppstod',
      title: 'Kan ikke logge inn',
    },
    passkey: {
      subtitle:
        'Bruk av passnøkkelen bekrefter at det er deg. Enheten din kan be om fingeravtrykk, ansiktsgjenkjenning eller skjermlås.',
      title: 'Bruk passnøkkelen din',
    },
    password: {
      actionLink: 'Bruk en annen metode',
      subtitle: 'for å fortsette til {{applicationName}}',
      title: 'Skriv inn passordet ditt',
    },
    passwordCompromised: {
      title: 'Passordet er kompromittert',
    },
    passwordPwned: {
      title: 'Passordet er kompromittert',
    },
    passwordUntrusted: {
      title: 'Passordet er ikke pålitelig',
    },
    phoneCode: {
      formTitle: 'Verifiseringskode',
      resendButton: 'Send kode på nytt',
      subtitle: 'for å fortsette til {{applicationName}}',
      title: 'Sjekk telefonen din',
    },
    phoneCodeMfa: {
      formTitle: 'Verifiseringskode',
      resendButton: 'Send kode på nytt',
      subtitle: 'For å fortsette, skriv inn verifiseringskoden sendt til telefonen din',
      title: 'Sjekk telefonen din',
    },
    protectCheck: {
      loading: undefined,
      retryButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    resetPassword: {
      formButtonPrimary: 'Tilbakestill passordet',
      requiredMessage:
        'En konto eksisterer allerede med en uverifisert e-postadresse. Vennligst tilbakestill passordet ditt av sikkerhetshensyn.',
      successMessage: 'Passordet ditt er blitt tilbakestilt. Logger deg inn, vennligst vent et øyeblikk.',
      title: 'Tilbakestill passordet',
    },
    resetPasswordMfa: {
      detailsLabel: 'Vi må bekrefte identiteten din før vi tilbakestiller passordet ditt.',
    },
    start: {
      actionLink: 'Opprett konto',
      actionLink__join_waitlist: 'Bli med på ventelisten',
      actionLink__use_email: 'Bruk e-post',
      actionLink__use_email_username: 'Bruk e-post eller brukernavn',
      actionLink__use_passkey: 'Bruk passnøkkel i stedet',
      actionLink__use_phone: 'Bruk telefon',
      actionLink__use_username: 'Bruk brukernavn',
      actionText: 'Ingen konto?',
      actionText__join_waitlist: 'Ønsker du tidlig tilgang?',
      alternativePhoneCodeProvider: {
        actionLink: 'Bruk en annen metode',
        label: '{{provider}}-telefonnummer',
        subtitle: 'Skriv inn telefonnummeret ditt for å få en verifiseringskode på {{provider}}.',
        title: 'Logg inn på {{applicationName}} med {{provider}}',
      },
      subtitle: 'for å fortsette til {{applicationName}}',
      subtitleCombined: undefined,
      title: 'Logg inn',
      titleCombined: 'Fortsett til {{applicationName}}',
    },
    totpMfa: {
      formTitle: 'Verifiseringskode',
      subtitle: 'For å fortsette, skriv inn verifiseringskoden generert av autentiseringsappen din',
      title: 'To-trinns verifisering',
    },
    web3Solana: {
      subtitle: 'Velg en lommebok nedenfor for å logge inn',
      title: 'Logg inn med Solana',
    },
  },
  signInEnterPasswordTitle: 'Skriv inn passordet ditt',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: 'Ikke mottatt kode? Send på nytt',
      subtitle: 'Skriv inn verifiseringskoden sendt til {{provider}}',
      title: 'Verifiser {{provider}}',
    },
    continue: {
      actionLink: 'Logg inn',
      actionText: 'Har du allerede en konto?',
      subtitle: 'for å fortsette til {{applicationName}}',
      title: 'Fyll ut manglende felt',
    },
    emailCode: {
      formSubtitle: 'Skriv inn verifiseringskoden som er sendt til e-postadressen din',
      formTitle: 'Verifiseringskode',
      resendButton: 'Send kode på nytt',
      subtitle: 'for å fortsette til {{applicationName}}',
      title: 'Verifiser e-posten din',
    },
    emailLink: {
      clientMismatch: {
        subtitle: 'For å fortsette, åpne verifiseringslenken på enheten og nettleseren du startet registreringen fra',
        title: 'Verifiseringslenken er ugyldig for denne enheten',
      },
      formSubtitle: 'Bruk verifiseringslenken som er sendt til e-postadressen din',
      formTitle: 'Verifiseringslenke',
      loading: {
        title: 'Registrerer deg...',
      },
      resendButton: 'Send lenke på nytt',
      subtitle: 'for å fortsette til {{applicationName}}',
      title: 'Verifiser e-posten din',
      verified: {
        title: 'Registreringen var vellykket',
      },
      verifiedSwitchTab: {
        subtitle: 'Gå tilbake til den nylig åpnede fanen for å fortsette',
        subtitleNewTab: 'Gå tilbake til forrige fane for å fortsette',
        title: 'E-posten ble verifisert',
      },
    },
    enterpriseConnections: {
      subtitle: 'Velg bedriftskontoen du ønsker å fortsette med.',
      title: 'Velg bedriftskonto',
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: 'Jeg godtar {{ privacyPolicyLink || link("Personvernerklæringen") }}',
        label__onlyTermsOfService: 'Jeg godtar {{ termsOfServiceLink || link("Bruksvilkårene") }}',
        label__termsOfServiceAndPrivacyPolicy:
          'Jeg godtar {{ termsOfServiceLink || link("Bruksvilkårene") }} og {{ privacyPolicyLink || link("Personvernerklæringen") }}',
      },
      continue: {
        subtitle: 'Vennligst les og godta vilkårene for å fortsette',
        title: 'Juridisk samtykke',
      },
    },
    phoneCode: {
      formSubtitle: 'Skriv inn verifiseringskoden som er sendt til telefonnummeret ditt',
      formTitle: 'Verifiseringskode',
      resendButton: 'Send kode på nytt',
      subtitle: 'for å fortsette til {{applicationName}}',
      title: 'Verifiser telefonen din',
    },
    protectCheck: {
      loading: undefined,
      retryButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    restrictedAccess: {
      actionLink: 'Logg inn',
      actionText: 'Har du allerede en konto?',
      blockButton__emailSupport: 'Send e-post til brukerstøtte',
      blockButton__joinWaitlist: 'Bli med på ventelisten',
      subtitle: 'Registrering er for øyeblikket deaktivert. Hvis du mener du bør ha tilgang, kontakt brukerstøtte.',
      subtitleWaitlist:
        'Registrering er for øyeblikket deaktivert. For å være den første som får vite når vi lanserer, bli med på ventelisten.',
      title: 'Begrenset tilgang',
    },
    start: {
      actionLink: 'Logg inn',
      actionLink__use_email: 'Bruk e-post i stedet',
      actionLink__use_phone: 'Bruk telefon i stedet',
      actionText: 'Har du allerede en konto?',
      alternativePhoneCodeProvider: {
        actionLink: 'Bruk en annen metode',
        label: '{{provider}}-telefonnummer',
        subtitle: 'Skriv inn telefonnummeret ditt for å få en verifiseringskode på {{provider}}.',
        title: 'Registrer deg på {{applicationName}} med {{provider}}',
      },
      subtitle: 'for å fortsette til {{applicationName}}',
      subtitleCombined: 'for å fortsette til {{applicationName}}',
      title: 'Opprett kontoen din',
      titleCombined: 'Opprett kontoen din',
    },
    web3Solana: {
      subtitle: 'Velg en lommebok nedenfor for å registrere deg',
      title: 'Registrer deg med Solana',
    },
  },
  socialButtonsBlockButton: 'Fortsett med {{provider|titleize}}',
  socialButtonsBlockButtonManyInView: '{{provider|titleize}}',
  taskChooseOrganization: {
    alerts: {
      organizationAlreadyExists:
        'En organisasjon eksisterer allerede for det oppdagede firmanavnet ({{organizationName}}) og {{organizationDomain}}. Bli med via invitasjon.',
    },
    chooseOrganization: {
      action__createOrganization: 'Opprett ny organisasjon',
      action__invitationAccept: 'Bli med',
      action__suggestionsAccept: 'Be om å bli med',
      subtitle: 'Bli med i en eksisterende organisasjon eller opprett en ny',
      subtitle__createOrganizationDisabled: 'Bli med i en eksisterende organisasjon',
      suggestionsAcceptedLabel: 'Venter på godkjenning',
      title: 'Velg en organisasjon',
    },
    createOrganization: {
      formButtonReset: 'Avbryt',
      formButtonSubmit: 'Fortsett',
      formFieldInputPlaceholder__name: 'Min organisasjon',
      formFieldInputPlaceholder__slug: 'min-organisasjon',
      formFieldLabel__name: 'Navn',
      formFieldLabel__slug: 'Slug',
      subtitle: 'Skriv inn organisasjonsdetaljene dine for å fortsette',
      title: 'Sett opp din organisasjon',
    },
    organizationCreationDisabled: {
      subtitle: 'Kontakt organisasjonsadministratoren din for en invitasjon.',
      title: 'Du må tilhøre en organisasjon',
    },
    signOut: {
      actionLink: 'Logg ut',
      actionText: 'Innlogget som {{identifier}}',
    },
  },
  taskResetPassword: {
    formButtonPrimary: 'Tilbakestill passord',
    signOut: {
      actionLink: 'Logg ut',
      actionText: 'Innlogget som {{identifier}}',
    },
    subtitle: 'Kontoen din krever et nytt passord før du kan fortsette',
    title: 'Tilbakestill passordet ditt',
  },
  taskSetupMfa: {
    badge: 'Oppsett av to-trinns verifisering',
    signOut: {
      actionLink: 'Logg ut',
      actionText: 'Innlogget som {{identifier}}',
    },
    smsCode: {
      addPhone: {
        formButtonPrimary: 'Fortsett',
        infoText:
          'En tekstmelding med en verifiseringskode vil bli sendt til dette telefonnummeret. Meldinger og datakostnader kan påløpe.',
      },
      addPhoneNumber: 'Legg til telefonnummer',
      cancel: 'Avbryt',
      subtitle: 'Velg telefonnummeret du vil bruke for SMS-kode to-trinns verifisering',
      success: {
        finishButton: 'Fortsett',
        message1:
          'To-trinns verifisering er nå aktivert. Ved innlogging må du skrive inn en verifiseringskode sendt til dette telefonnummeret som et ekstra trinn.',
        message2:
          'Lagre disse sikkerhetskodene og oppbevar dem på et trygt sted. Hvis du mister tilgang til autentiseringsenheten din, kan du bruke sikkerhetskoder for å logge inn.',
        title: 'SMS-kodeverifisering aktivert',
      },
      title: 'Legg til SMS-kodeverifisering',
      verifyPhone: {
        formButtonPrimary: 'Fortsett',
        formTitle: 'Verifiseringskode',
        resendButton: 'Ikke mottatt kode? Send på nytt',
        subtitle: 'Skriv inn verifiseringskoden sendt til',
        title: 'Verifiser telefonnummeret ditt',
      },
    },
    start: {
      methodSelection: {
        phoneCode: 'SMS-kode',
        totp: 'Autentiseringsapp',
      },
      subtitle: 'Velg hvilken metode du foretrekker for å beskytte kontoen din med et ekstra sikkerhetslag',
      title: 'Sett opp to-trinns verifisering',
    },
    totpCode: {
      addAuthenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Skann QR-kode i stedet',
        buttonUnableToScan__nonPrimary: 'Kan ikke skanne QR-kode?',
        formButtonPrimary: 'Fortsett',
        formButtonReset: 'Avbryt',
        infoText__ableToScan:
          'Sett opp en ny innloggingsmetode i autentiseringsappen din og skann følgende QR-kode for å koble den til kontoen din.',
        infoText__unableToScan:
          'Sett opp en ny innloggingsmetode i autentiseringsappen og skriv inn nøkkelen som er oppgitt nedenfor.',
        inputLabel__unableToScan1:
          'Sørg for at tidsbaserte eller engangspassord er aktivert, og fullfør deretter koblingen av kontoen din.',
      },
      success: {
        finishButton: 'Fortsett',
        message1:
          'To-trinns verifisering er nå aktivert. Ved innlogging må du skrive inn en verifiseringskode fra denne autentiseringsappen som et ekstra trinn.',
        message2:
          'Lagre disse sikkerhetskodene og oppbevar dem på et trygt sted. Hvis du mister tilgang til autentiseringsenheten din, kan du bruke sikkerhetskoder for å logge inn.',
        title: 'Autentiseringsapp-verifisering aktivert',
      },
      title: 'Legg til autentiseringsapp',
      verifyTotp: {
        formButtonPrimary: 'Fortsett',
        formButtonReset: 'Avbryt',
        formTitle: 'Verifiseringskode',
        subtitle: 'Skriv inn verifiseringskoden generert av autentiseringsappen din',
        title: 'Legg til autentiseringsapp',
      },
    },
  },
  unstable__errors: {
    action_blocked: undefined,
    already_a_member_in_organization: '{{email}} er allerede medlem av organisasjonen.',
    api_key_name_already_exists: 'API-nøkkelnavnet finnes allerede.',
    api_key_usage_exceeded: 'Du har nådd bruksgrensen din. Du kan fjerne grensen ved å oppgradere til en betalt plan.',
    avatar_file_size_exceeded: 'Filstørrelsen overskrider maksgrensen på 10 MB. Vennligst velg en mindre fil.',
    avatar_file_type_invalid: 'Filtypen støttes ikke. Vennligst last opp et JPG-, PNG-, GIF- eller WEBP-bilde.',
    captcha_invalid:
      'Registreringen mislyktes på grunn av mislykkede sikkerhetsvalideringer. Vennligst oppdater siden og prøv igjen, eller ta kontakt med brukerstøtte for mer hjelp.',
    captcha_unavailable:
      'Registreringen mislyktes på grunn av mislykkede bot-valideringer. Vennligst oppdater siden og prøv igjen, eller ta kontakt med brukerstøtte for mer hjelp.',
    form_code_incorrect: undefined,
    form_email_address_blocked:
      'Midlertidige e-posttjenester støttes ikke. Vennligst bruk din vanlige e-postadresse for å opprette en konto.',
    form_identifier_exists__email_address: undefined,
    form_identifier_exists__phone_number: undefined,
    form_identifier_exists__username: undefined,
    form_identifier_not_found: 'Vi klarte ikke finne en konto med disse detaljene.',
    form_new_password_matches_current: 'Det nye passordet kan ikke være det samme som det nåværende passordet.',
    form_param_format_invalid: undefined,
    form_param_format_invalid__email_address: 'E-postadressen må være en gyldig e-postadresse',
    form_param_format_invalid__phone_number: 'Telefonnummeret må være i et gyldig internasjonalt format',
    form_param_max_length_exceeded__first_name: 'Fornavn kan ikke være lengre enn 256 bokstaver.',
    form_param_max_length_exceeded__last_name: 'Etternavn kan ikke være lengre enn 256 bokstaver.',
    form_param_max_length_exceeded__name: 'Navn kan ikke være lengre enn 256 bokstaver.',
    form_param_nil: undefined,
    form_param_type_invalid: undefined,
    form_param_type_invalid__email_address: undefined,
    form_param_type_invalid__phone_number: undefined,
    form_param_value_invalid: undefined,
    form_password_compromised__sign_in: undefined,
    form_password_incorrect: undefined,
    form_password_length_too_short: 'Passordet ditt er for kort. Det må være minst 8 tegn langt.',
    form_password_not_strong_enough: 'Passordet ditt er ikke sterkt nok.',
    form_password_or_identifier_incorrect:
      'Passordet eller e-postadressen er feil. Prøv igjen eller bruk en annen metode.',
    form_password_pwned:
      'Dette passordet er funnet som en del av et datainnbrudd og kan ikke brukes. Vennligst prøv et annet passord.',
    form_password_pwned__sign_in:
      'Dette passordet er funnet som en del av et datainnbrudd og kan ikke brukes. Vennligst tilbakestill passordet ditt.',
    form_password_size_in_bytes_exceeded:
      'Passordet ditt har overskredet maksimalt antall byte tillatt. Vennligst forkort det eller fjern noen spesialtegn.',
    form_password_untrusted__sign_in:
      'Passordet ditt kan være kompromittert. For å beskytte kontoen din, fortsett med en alternativ innloggingsmetode. Du vil bli bedt om å tilbakestille passordet etter innlogging.',
    form_password_validation_failed: 'Feil passord',
    form_username_invalid_character: undefined,
    form_username_invalid_length: 'Brukernavnet ditt må være mellom {{min_length}} og {{max_length}} tegn langt.',
    form_username_needs_non_number_char: 'Brukernavnet ditt må inneholde minst ett ikke-numerisk tegn.',
    identification_deletion_failed: 'Du kan ikke slette din siste identifikasjon.',
    insufficient_seats_change_plan: undefined,
    insufficient_seats_contact_support: undefined,
    not_allowed_access:
      "E-postadressen eller telefonnummeret ditt er ikke tillatt for registrering. Dette kan være på grunn av bruk av '+', '=', '#' eller '.' i e-postadressen din, bruk av et domenn som er tilknyttet en midlertidig e-posttjeneste, eller eksplisitt blokkering. Hvis du mener dette er en feil, vennligst kontakt støtte.",
    oauth_access_denied: undefined,
    organization_domain_blocked: undefined,
    organization_domain_common: undefined,
    organization_domain_exists_for_enterprise_connection: undefined,
    organization_membership_quota_exceeded:
      'Du har nådd grensen for organisasjonsmedlemskap, inkludert utestående invitasjoner.',
    organization_minimum_permissions_needed: undefined,
    organization_not_found_or_unauthorized:
      'Du er ikke lenger medlem av denne organisasjonen. Vennligst velg eller opprett en annen.',
    organization_not_found_or_unauthorized_with_create_organization_disabled:
      'Du er ikke lenger medlem av denne organisasjonen. Vennligst velg en annen.',
    passkey_already_exists: 'En passnøkkel er allerede registrert med denne enheten.',
    passkey_not_supported: 'Passnøkler støttes ikke på denne enheten.',
    passkey_pa_not_supported: 'Registrering krever en plattformautentisering, men enheten støtter det ikke.',
    passkey_registration_cancelled: 'Passnøkkelregistreringen ble avbrutt eller tidsavbrutt.',
    passkey_retrieval_cancelled: 'Passnøkkelverifiseringen ble avbrutt eller tidsavbrutt.',
    passwordComplexity: {
      maximumLength: 'mindre enn {{length}} tegn',
      minimumLength: '{{length}} eller flere tegn',
      requireLowercase: 'en liten bokstav',
      requireNumbers: 'et tall',
      requireSpecialCharacter: 'et spesialtegn',
      requireUppercase: 'en stor bokstav',
      sentencePrefix: 'Passordet ditt må inneholde',
    },
    phone_number_exists: 'Dette telefonnummeret er allerede i bruk. Vennligst bruk et annet telefonnummer.',
    protect_check_aborted: undefined,
    protect_check_already_resolved: undefined,
    protect_check_execution_failed: undefined,
    protect_check_invalid_script: undefined,
    protect_check_invalid_sdk_url: undefined,
    protect_check_script_load_failed: undefined,
    protect_check_timed_out: undefined,
    protect_check_unsupported_environment: undefined,
    session_exists: 'Du er allerede logget inn.',
    web3_missing_identifier: 'En Web3-lommebokutvidelse ble ikke funnet. Installer en for å fortsette.',
    web3_signature_request_rejected: 'Du avviste signaturforespørselen. Prøv igjen for å fortsette.',
    web3_solana_signature_generation_failed:
      'Det oppstod en feil under generering av signaturen. Prøv igjen for å fortsette.',
    zxcvbn: {
      couldBeStronger: 'Passordet ditt fungerer, men det kan være sterkere. Prøv å legge til flere tegn.',
      goodPassword: 'Godt jobbet. Dette er et utmerket passord.',
      notEnough: 'Passordet ditt er ikke sterkt nok.',
      suggestions: {
        allUppercase: 'Stor bokstav på noen, men ikke alle bokstaver.',
        anotherWord: 'Legg til flere ord som er mindre vanlige.',
        associatedYears: 'Unngå år som er knyttet til deg.',
        capitalization: 'Sett stor bokstav på mer enn den første bokstaven.',
        dates: 'Unngå datoer og år som er knyttet til deg.',
        l33t: "Unngå forutsigbare bokstavbytter som '@' for 'a'.",
        longerKeyboardPattern: 'Bruk lengre tastaturmønstre og endre skrivretning flere ganger.',
        noNeed: 'Du kan lage sterke passord uten å bruke symboler, tall eller store bokstaver.',
        pwned: 'Hvis du bruker dette passordet andre steder, bør du endre det.',
        recentYears: 'Unngå nylige år.',
        repeated: 'Unngå gjentatte ord og tegn.',
        reverseWords: 'Unngå omvendte stavelser av vanlige ord.',
        sequences: 'Unngå vanlige tegnsekvenser.',
        useWords: 'Bruk flere ord, men unngå vanlige fraser.',
      },
      warnings: {
        common: 'Dette er et vanlig brukt passord.',
        commonNames: 'Vanlige navn og etternavn er lett å gjette.',
        dates: 'Datoer er lett å gjette.',
        extendedRepeat: 'Gjentatte tegnmønstre som "abcabcabc" er lett å gjette.',
        keyPattern: 'Korte tastaturmønstre er lett å gjette.',
        namesByThemselves: 'Enkelt navn eller etternavn er lett å gjette.',
        pwned: 'Passordet ditt ble eksponert i et datainnbrudd på internett.',
        recentYears: 'Nylige år er lett å gjette.',
        sequences: 'Vanlige tegnsekvenser som "abc" er lett å gjette.',
        similarToCommon: 'Dette ligner på et vanlig brukt passord.',
        simpleRepeat: 'Gjentatte tegn som "aaa" er lett å gjette.',
        straightRow: 'Rette rader med tastene på tastaturet ditt er lett å gjette.',
        topHundred: 'Dette er et ofte brukt passord.',
        topTen: 'Dette er et mye brukt passord.',
        userInputs: 'Det bør ikke være personlige eller sidetilknyttede data.',
        wordByItself: 'Enkeltord er lett å gjette.',
      },
    },
  },
  userButton: {
    action__addAccount: 'Legg til konto',
    action__closeUserMenu: 'Lukk brukermeny',
    action__manageAccount: 'Administrer konto',
    action__openUserMenu: 'Åpne brukermeny',
    action__signOut: 'Logg ut',
    action__signOutAll: 'Logg ut av alle kontoer',
    label__accountActions: 'Kontohandlinger',
    label__activeSessions: 'Aktive økter',
    label__userButtonPopover: 'Kontopanel',
  },
  userProfile: {
    apiKeysPage: {
      title: 'API-nøkler',
    },
    backupCodePage: {
      actionLabel__copied: 'Kopiert!',
      actionLabel__copy: 'Kopier alle',
      actionLabel__download: 'Last ned .txt',
      actionLabel__print: 'Skriv ut',
      infoText1: 'Sikkerhetskoder vil bli aktivert for denne kontoen.',
      infoText2:
        'Hold sikkerhetskodene hemmelige og oppbevar dem sikkert. Du kan generere nye sikkerhetskoder hvis du mistenker at de er kompromittert.',
      subtitle__codelist: 'Oppbevar dem sikkert og hold dem hemmelige.',
      successMessage:
        'Sikkerhetskoder er nå aktivert. Du kan bruke en av disse til å logge inn på kontoen din hvis du mister tilgangen til autentiseringsenheten. Hver kode kan bare brukes én gang.',
      successSubtitle:
        'Du kan bruke en av disse til å logge inn på kontoen din hvis du mister tilgangen til autentiseringsenheten.',
      title: 'Legg til sikkerhetskopieringskodeverifisering',
      title__codelist: 'Sikkerhetskoder',
    },
    billingPage: {
      accountCreditsSection: {
        title: undefined,
        viewHistory: undefined,
      },
      creditHistoryPage: {
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
      },
      paymentHistorySection: {
        empty: 'Ingen betalingshistorikk',
        notFound: 'Betalingsforsøk ikke funnet',
        tableHeader__amount: 'Beløp',
        tableHeader__date: 'Dato',
        tableHeader__status: 'Status',
      },
      paymentMethodsSection: {
        actionLabel__default: 'Sett som standard',
        actionLabel__remove: 'Fjern',
        add: 'Legg til ny betalingsmetode',
        addSubtitle: 'Legg til en ny betalingsmetode for kontoen din.',
        cancelButton: 'Avbryt',
        formButtonPrimary__add: 'Legg til betalingsmetode',
        formButtonPrimary__pay: 'Betal {{amount}}',
        payWithTestCardButton: 'Betal med testkort',
        removeMethod: {
          messageLine1: '{{identifier}} vil bli fjernet fra denne kontoen.',
          messageLine2:
            'Du vil ikke lenger kunne bruke denne betalingskilden, og eventuelle gjentakende abonnementer avhengig av den vil slutte å fungere.',
          successMessage: '{{paymentMethod}} har blitt fjernet fra kontoen din.',
          title: 'Fjern betalingsmetode',
        },
        title: 'Betalingsmetoder',
      },
      start: {
        headerTitle__payments: 'Betalinger',
        headerTitle__plans: 'Planer',
        headerTitle__statements: 'Kontoutskrifter',
        headerTitle__subscriptions: 'Abonnement',
      },
      statementsSection: {
        empty: 'Ingen kontoutskrifter å vise',
        itemCaption__paidForPlan: 'Betalt for {{plan}} {{period}}-plan',
        itemCaption__payerCredit: 'Kreditt fra kontosaldo',
        itemCaption__proratedCredit: 'Forholdsmessig kreditt for delvis bruk av forrige abonnement',
        itemCaption__subscribedAndPaidForPlan: 'Abonnert og betalt for {{plan}} {{period}}-plan',
        notFound: 'Kontoutskrift ikke funnet',
        tableHeader__amount: 'Beløp',
        tableHeader__date: 'Dato',
        title: 'Kontoutskrifter',
        totalPaid: 'Totalt betalt',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: 'Administrer',
        actionLabel__newSubscription: 'Abonner på en plan',
        actionLabel__switchPlan: 'Bytt plan',
        overview: undefined,
        tableHeader__edit: 'Rediger',
        tableHeader__plan: 'Plan',
        tableHeader__startDate: 'Startdato',
        title: 'Abonnement',
      },
      subscriptionsSection: {
        actionLabel__default: 'Administrer',
      },
      switchPlansSection: {
        title: 'Bytt plan',
      },
      title: 'Fakturering',
    },
    connectedAccountPage: {
      formHint: 'Velg en tilbyder for å koble til kontoen din.',
      formHint__noAccounts: 'Det er ingen tilgjengelige eksterne konto-tilbydere.',
      removeResource: {
        messageLine1: '{{identifier}} vil bli fjernet fra denne kontoen.',
        messageLine2:
          'Du vil ikke lenger kunne bruke denne tilknyttede kontoen, og eventuelle avhengige funksjoner vil ikke lenger fungere.',
        successMessage: '{{connectedAccount}} har blitt fjernet fra kontoen din.',
        title: 'Fjern tilknyttet konto',
      },
      socialButtonsBlockButton: 'Koble til {{provider|titleize}}-konto',
      successMessage: 'Tilbyderen har blitt lagt til kontoen din.',
      title: 'Legg til tilknyttet konto',
    },
    deletePage: {
      actionDescription: 'Skriv inn "Slett konto" under for å fortsette.',
      confirm: 'Slett konto',
      messageLine1:
        'Er du sikker på at du vil slette kontoen din? Noen tilknyttede data kan beholdes. For å be om full sletting av data, vennligst kontakt kundestøtte.',
      messageLine2: 'Denne handlingen er permanent og kan ikke reverseres.',
      title: 'Slett konto',
    },
    emailAddressPage: {
      emailCode: {
        formHint: 'En e-post med en verifiseringskode vil bli sendt til denne e-postadressen.',
        formSubtitle: 'Skriv inn verifiseringskoden som er sendt til {{identifier}}',
        formTitle: 'Verifiseringskode',
        resendButton: 'Send kode på nytt',
        successMessage: 'E-posten {{identifier}} har blitt lagt til kontoen din.',
      },
      emailLink: {
        formHint: 'En e-post med en verifiseringslenke vil bli sendt til denne e-postadressen.',
        formSubtitle: 'Klikk på verifiseringslenken i e-posten sendt til {{identifier}}',
        formTitle: 'Verifiseringslenke',
        resendButton: 'Send lenke på nytt',
        successMessage: 'E-posten {{identifier}} har blitt lagt til kontoen din.',
      },
      enterpriseSSOLink: {
        formButton: 'Klikk for å logge inn',
        formSubtitle: 'Fullfør innloggingen med {{identifier}}',
      },
      formHint: 'Du må verifisere denne e-postadressen før den kan legges til kontoen din.',
      removeResource: {
        messageLine1: '{{identifier}} vil bli fjernet fra denne kontoen.',
        messageLine2: 'Du vil ikke lenger kunne logge inn med denne e-postadressen.',
        successMessage: '{{emailAddress}} har blitt fjernet fra kontoen din.',
        title: 'Fjern e-postadresse',
      },
      title: 'Legg til e-postadresse',
      verifyTitle: 'Verifiser e-postadresse',
    },
    formButtonPrimary__add: 'Legg til',
    formButtonPrimary__continue: 'Fortsett',
    formButtonPrimary__finish: 'Fullfør',
    formButtonPrimary__remove: 'Fjern',
    formButtonPrimary__save: 'Lagre',
    formButtonReset: 'Avbryt',
    mfaPage: {
      formHint: 'Velg en metode for å legge til.',
      title: 'Legg til to-trinns verifisering',
    },
    mfaPhoneCodePage: {
      backButton: 'Bruk eksisterende nummer',
      primaryButton__addPhoneNumber: 'Legg til et telefonnummer',
      removeResource: {
        messageLine1: '{{identifier}} vil ikke lenger motta verifiseringskoder ved pålogging.',
        messageLine2: 'Kontoen din kan bli mindre sikker. Er du sikker på at du vil fortsette?',
        successMessage: 'SMS-kode to-trinns verifisering er fjernet for {{mfaPhoneCode}}',
        title: 'Fjern to-trinns verifisering',
      },
      subtitle__availablePhoneNumbers:
        'Velg et telefonnummer for å registrere deg for SMS-kode to-trinns verifisering.',
      subtitle__unavailablePhoneNumbers:
        'Det er ingen tilgjengelige telefonnummer å registrere seg for SMS-kode to-trinns verifisering.',
      successMessage1:
        'Ved innlogging vil du måtte skrive inn en verifiseringskode sendt til dette telfonnummeret som et tilleggssteg.',
      successMessage2:
        'Ta vare på disse reservekodene og oppbevar dem på et trygt sted. Hvis du mister tilgang til autentiseringsenheten din kan du bruke reservekodene for å logge inn.',
      successTitle: 'SMS-kodeverifisering lagt til',
      title: 'Legg til SMS-kodeverifisering',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Skan QR-kode i stedet',
        buttonUnableToScan__nonPrimary: 'Kan ikke skanne QR-kode?',
        infoText__ableToScan:
          'Sett opp en ny innloggingsmetode i autentiseringsappen din og skann følgende QR-kode for å koble den til kontoen din.',
        infoText__unableToScan:
          'Sett opp en ny innloggingsmetode i autentiseringsappen og skriv inn nøkkelen som er oppgitt nedenfor.',
        inputLabel__unableToScan1:
          'Sørg for at tidsbaserte eller engangspassord er aktivert, og fullfør deretter koblingen av kontoen din.',
        inputLabel__unableToScan2:
          'Alternativt, hvis autentiseringsappen din støtter TOTP URI-er, kan du også kopiere hele URI-en.',
      },
      removeResource: {
        messageLine1: 'Verifiseringskoder fra denne autentiseringsappen vil ikke lenger være påkrevd ved pålogging.',
        messageLine2: 'Kontoen din kan bli mindre sikker. Er du sikker på at du vil fortsette?',
        successMessage: 'To-trinns verifisering via autentiseringsappen er fjernet.',
        title: 'Fjern to-trinns verifisering',
      },
      successMessage:
        'To-trinns verifisering er nå aktivert. Når du logger inn, må du angi en verifiseringskode fra denne autentiseringsappen som et ekstra trinn.',
      title: 'Legg til autentiseringsapp',
      verifySubtitle: 'Skriv inn verifiseringskoden generert av autentiseringsappen din',
      verifyTitle: 'Verifiseringskode',
    },
    mobileButton__menu: 'Meny',
    navbar: {
      account: 'Profil',
      apiKeys: 'API-nøkler',
      billing: 'Fakturering',
      description: 'Administrer kontoinformasjonen din.',
      security: 'Sikkerhet',
      title: 'Konto',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: '{{name}} vil bli fjernet fra denne kontoen.',
        title: 'Fjern passnøkkel',
      },
      subtitle__rename: 'Du kan endre navnet på passnøkkelen for å gjøre den lettere å finne.',
      title__rename: 'Gi passnøkkel nytt navn',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        'Det er anbefalt å logge ut av alle de andre enhetene dine som kan ha brukt ditt gamle passord.',
      readonly: 'Passordet ditt kan for øyeblikket ikke endres fordi du kun kan logge inn via bedriftstilkoblingen.',
      successMessage__set: 'Passordet ditt er satt.',
      successMessage__signOutOfOtherSessions: 'Alle andre enheter har blitt logget ut.',
      successMessage__update: 'Passordet ditt har blitt oppdatert.',
      title__set: 'Sett passord',
      title__update: 'Endre passord',
    },
    phoneNumberPage: {
      infoText: 'En tekstmelding med en verifiseringslenke vil bli sendt til dette telefonnummeret.',
      removeResource: {
        messageLine1: '{{identifier}} vil bli fjernet fra denne kontoen.',
        messageLine2: 'Du vil ikke lenger kunne logge inn med dette telefonnummeret.',
        successMessage: '{{phoneNumber}} har blitt fjernet fra kontoen din.',
        title: 'Fjern telefonnummer',
      },
      successMessage: '{{identifier}} har blitt lagt til kontoen din.',
      title: 'Legg til telefonnummer',
      verifySubtitle: 'Skriv inn verifiseringskoden som ble sendt til {{identifier}}',
      verifyTitle: 'Verifiser telefonnummer',
    },
    plansPage: {
      title: 'Planer',
    },
    profilePage: {
      fileDropAreaHint: 'Last opp et JPG, PNG, GIF eller WEBP-bilde som er mindre enn 10 MB',
      imageFormDestructiveActionSubtitle: 'Fjern bilde',
      imageFormSubtitle: 'Last opp bilde',
      imageFormTitle: 'Profilbilde',
      readonly: 'Informasjonen om profilen din er levert av bedriftstilkoblingen og kan ikke redigeres.',
      successMessage: 'Profilen din har blitt oppdatert.',
      title: 'Oppdater profil',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'Logg ut fra enhet',
        title: 'Aktive enheter',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'Prøv på nytt',
        actionLabel__reauthorize: 'Autoriser nå',
        destructiveActionTitle: 'Fjern',
        primaryButton: 'Koble til konto',
        subtitle__disconnected: 'Denne kontoen har blitt frakoblet.',
        subtitle__reauthorize:
          'De påkrevde tillatelsene har blitt oppdatert, og du kan oppleve begrenset funksjonalitet. Vennligst autoriser denne applikasjonen på nytt for å unngå problemer',
        title: 'Tilkoblede kontoer',
      },
      dangerSection: {
        deleteAccountButton: 'Slett konto',
        title: 'Fare',
      },
      emailAddressesSection: {
        destructiveAction: 'Fjern e-postadresse',
        detailsAction__nonPrimary: 'Angi som primær',
        detailsAction__primary: 'Fullfør verifisering',
        detailsAction__unverified: 'Fullfør verifisering',
        primaryButton: 'Legg til en e-postadresse',
        title: 'E-postadresser',
      },
      enterpriseAccountsSection: {
        primaryButton: 'Koble til konto',
        title: 'Bedriftskontoer',
      },
      headerTitle__account: 'Konto',
      headerTitle__security: 'Sikkerhet',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'Generer koder på nytt',
          headerTitle: 'Sikkerhetskoder',
          subtitle__regenerate:
            'Få en ny serie med sikre sikkerhetskoder. Tidligere sikkerhetskoder vil bli slettet og kan ikke brukes.',
          title__regenerate: 'Generer nye sikkerhetskoder',
        },
        phoneCode: {
          actionLabel__setDefault: 'Angi som standard',
          destructiveActionLabel: 'Fjern telefonnummer',
        },
        primaryButton: 'Legg til to-trinns verifisering',
        title: 'To-trinns verifisering',
        totp: {
          destructiveActionTitle: 'Fjern',
          headerTitle: 'Autentiseringsapplikasjon',
        },
      },
      passkeysSection: {
        menuAction__destructive: 'Fjern',
        menuAction__rename: 'Gi nytt navn',
        primaryButton: 'Legg til en passnøkkel',
        title: 'Passnøkler',
      },
      passwordSection: {
        primaryButton__setPassword: 'Opprett passord',
        primaryButton__updatePassword: 'Endre passord',
        title: 'Passord',
      },
      phoneNumbersSection: {
        destructiveAction: 'Fjern telefonnummer',
        detailsAction__nonPrimary: 'Angi som primær',
        detailsAction__primary: 'Fullfør verifisering',
        detailsAction__unverified: 'Fullfør verifisering',
        primaryButton: 'Legg til et telefonnummer',
        title: 'Telefonnumre',
      },
      profileSection: {
        primaryButton: 'Oppdater profil',
        title: 'Profil',
      },
      usernameSection: {
        primaryButton__setUsername: 'Angi brukernavn',
        primaryButton__updateUsername: 'Endre brukernavn',
        title: 'Brukernavn',
      },
      web3WalletsSection: {
        destructiveAction: 'Fjern lommebok',
        detailsAction__nonPrimary: 'Angi som primær',
        primaryButton: 'Web3-lommebøker',
        title: 'Web3-lommebøker',
        web3SelectSolanaWalletScreen: {
          subtitle: 'Velg en Solana-lommebok for å koble den til kontoen din.',
          title: 'Legg til en Solana-lommebok',
        },
      },
    },
    usernamePage: {
      successMessage: 'Brukernavnet ditt har blitt oppdatert.',
      title__set: 'Oppdater brukernavn',
      title__update: 'Oppdater brukernavn',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} vil bli fjernet fra denne kontoen.',
        messageLine2: 'Du vil ikke lenger kunne logge inn med denne web3-lommeboken.',
        successMessage: '{{web3Wallet}} har blitt fjernet fra kontoen din.',
        title: 'Fjern web3-lommebok',
      },
      subtitle__availableWallets: 'Velg en web3-lommebok for å koble til kontoen din.',
      subtitle__unavailableWallets: 'Det er ingen tilgjengelige web3-lommebøker.',
      successMessage: 'Lommeboken har blitt lagt til kontoen din.',
      title: 'Legg til web3-lommebok',
      web3WalletButtonsBlockButton: '{{provider|titleize}}',
    },
  },
  waitlist: {
    start: {
      actionLink: 'Logg inn',
      actionText: 'Har du allerede tilgang?',
      formButton: 'Bli med på ventelisten',
      subtitle: 'Skriv inn e-postadressen din, så gir vi deg beskjed når plassen din er klar',
      title: 'Bli med på ventelisten',
    },
    success: {
      message: 'Du vil bli omdirigert snart...',
      subtitle: 'Vi tar kontakt når plassen din er klar',
      title: 'Takk for at du ble med på ventelisten!',
    },
  },
  web3SolanaWalletButtons: {
    connect: 'Koble til med {{walletName}}',
    continue: 'Fortsett med {{walletName}}',
    noneAvailable:
      'Ingen Solana Web3-lommebøker ble funnet. Installer en Web3-støttet {{ solanaWalletsLink || link("wallet extension") }}.',
  },
} as const;
